import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, UploadCloudIcon, ScaleIcon, TvIcon, ConstructionIcon, TruckIcon } from 'lucide-react';
export type ServiceType = 'internet-audit' | 'tv-audit' | 'new-service' | 'relocation' | 'website-transfer' | 'general';
const steps = [{
  id: 1,
  label: 'Contact'
}, {
  id: 2,
  label: 'Details'
}, {
  id: 3,
  label: 'Specifics'
}, {
  id: 4,
  label: 'Timeline'
}, {
  id: 5,
  label: 'Review'
}];
export function QuoteModal({
  isOpen,
  onClose,
  serviceType = 'general'
}: {
  isOpen: boolean;
  onClose: () => void;
  serviceType?: ServiceType;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});
  if (!isOpen) return null;
  const getServiceContent = () => {
    switch (serviceType) {
      case 'internet-audit':
        return {
          title: 'Lower Your Internet Bill',
          subtitle: 'Our audit uncovers overcharges and negotiates better rates for your business internet.',
          icon: ScaleIcon,
          color: 'bg-[#22A39F]'
        };
      case 'tv-audit':
        return {
          title: 'Lower Your TV Bill',
          subtitle: 'Stop overpaying for commercial TV. We find the best packages and hidden savings.',
          icon: TvIcon,
          color: 'bg-amber-500'
        };
      case 'new-service':
        return {
          title: 'New Service Installation',
          subtitle: 'Get a custom quote for your new business internet, phone, or security setup.',
          icon: ConstructionIcon,
          color: 'bg-[#FFD700]',
          textColor: 'text-[#011B33]'
        };
      case 'relocation':
        return {
          title: 'Seamless Relocation',
          subtitle: 'Moving your business? We coordinate your telecom transfer for zero downtime.',
          icon: TruckIcon,
          color: 'bg-[#011B33]'
        };
      default:
        return {
          title: 'Get Your Free Telecom Audit',
          subtitle: 'Answer a few quick questions so we can identify savings opportunities for your business.',
          icon: CheckCircleIcon,
          color: 'bg-primary'
        };
    }
  };
  const content = getServiceContent();
  const Icon = content.icon;
  const getServiceTitle = () => {
    switch (serviceType) {
      case 'internet-audit':
        return 'Internet Bill Audit';
      case 'tv-audit':
        return 'TV Bill Audit';
      case 'new-service':
        return 'New Service Installation';
      case 'relocation':
        return 'Business Relocation';
      case 'website-transfer':
        return 'Website Service Questionnaire';
      default:
        return 'Get a Custom Quote';
    }
  };
  const getServiceDescription = () => {
    switch (serviceType) {
      case 'internet-audit':
        return 'Upload your current internet bill and our AI will find hidden savings.';
      case 'tv-audit':
        return 'Let us analyze your TV service costs and negotiate better rates.';
      case 'new-service':
        return 'Tell us about your new location and the services you need.';
      case 'relocation':
        return 'Moving soon? We ensure your tech moves with you seamlessly.';
      case 'website-transfer':
        return 'Tell us about your website needs so we can provide the best solution.';
      default:
        return 'Fill out the form below and our experts will get back to you with a tailored solution.';
    }
  };
  const handleNext = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };
  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  const resetAndClose = () => {
    setIsSubmitted(false);
    setCurrentStep(1);
    setFormData({});
    onClose();
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-366">
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={resetAndClose} data-id="element-367"></div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-368">
        {/* Header */}
        <div className={`${content.color} p-6 md:p-8 ${content.textColor || 'text-white'} relative shrink-0`} data-id="element-369">
          <button onClick={resetAndClose} className={`absolute top-6 right-6 ${content.textColor ? 'text-slate-900/70 hover:text-slate-900' : 'text-white/70 hover:text-white'} transition-colors`} data-id="element-370">
            <XIcon className="w-6 h-6" data-id="element-371" />
          </button>
          <div className="flex items-center gap-4 mb-2" data-id="element-372">
            <div className={`w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center`} data-id="element-373">
              <Icon className="w-6 h-6" data-id="element-374" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" data-id="element-375">{content.title}</h2>
          </div>
          <p className="opacity-90 text-sm md:text-base" data-id="element-376">{content.subtitle}</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8" data-id="element-377">
          {isSubmitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center py-12" data-id="element-378">
              <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-379">
                <CheckCircleIcon className="w-10 h-10 text-accent" data-id="element-380" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-381">
                Request Received ✅
              </h3>
              <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-382">
                Thanks for requesting your {content.title}. Our team will review
                your details and contact you shortly.
              </p>
              <button onClick={resetAndClose} className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-383">
                Close
              </button>
            </motion.div> : <>
              {/* Progress Tracker */}
              <div className="mb-8" data-id="element-384">
                <div className="flex items-center justify-between relative" data-id="element-385">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-100 -z-10" data-id="element-386"></div>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-accent transition-all duration-300 -z-10" style={{
                width: `${(currentStep - 1) / 4 * 100}%`
              }} data-id="element-387"></div>
                  {steps.map(step => <div key={step.id} className="flex flex-col items-center gap-2" data-id="element-388">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${currentStep >= step.id ? 'bg-accent text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-389">
                        {step.id}
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-390">
                        {step.label}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Form Steps */}
              <form onSubmit={handleSubmit} data-id="element-391">
                <AnimatePresence mode="wait" data-id="element-392">
                  {currentStep === 1 && <motion.div key="step1" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-393">
                      <div className="mb-6" data-id="element-394">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-395">
                          Contact Information
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-396">
                          Where should we send your custom quote?
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-397">
                        <div data-id="element-398">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-399">
                            Business Name
                          </label>
                          <input type="text" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="Acme Corp" data-id="element-400" />
                        </div>
                        <div data-id="element-401">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-402">
                            Full Name
                          </label>
                          <input type="text" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="John Doe" data-id="element-403" />
                        </div>
                        <div data-id="element-404">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-405">
                            Work Email
                          </label>
                          <input type="email" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="john@acmecorp.com" data-id="element-406" />
                        </div>
                        <div data-id="element-407">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-408">
                            Phone Number
                          </label>
                          <input type="tel" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="(555) 123-4567" data-id="element-409" />
                        </div>
                      </div>
                    </motion.div>}

                  {currentStep === 2 && <motion.div key="step2" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-410">
                      <div className="mb-6" data-id="element-411">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-412">
                          Current Provider Details
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-413">
                          Help us understand your current setup.
                        </p>
                      </div>
                      <div className="space-y-4" data-id="element-414">
                        <div data-id="element-415">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-416">
                            Current Provider
                          </label>
                          <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="e.g. AT&T, Comcast, Verizon" data-id="element-417" />
                        </div>
                        <div data-id="element-418">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-419">
                            Estimated Monthly Spend
                          </label>
                          <div className="relative" data-id="element-420">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-421">
                              $
                            </span>
                            <input type="number" className="w-full pl-8 pr-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="0.00" data-id="element-422" />
                          </div>
                        </div>
                        <div className="md:col-span-2" data-id="element-423">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-424">
                            Upload Current Bill (Optional)
                          </label>
                          <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center bg-surface hover:bg-slate-100 transition-colors cursor-pointer" data-id="element-425">
                            <UploadCloudIcon className="w-8 h-8 text-slate-400 mb-2" data-id="element-426" />
                            <span className="text-sm text-slate-500" data-id="element-427">
                              Click to upload or drag and drop
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>}

                  {currentStep === 3 && <motion.div key="step3" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-428">
                      <div className="mb-6" data-id="element-429">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-430">
                          Service Specifics
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-431">
                          Tell us more about what you need.
                        </p>
                      </div>
                      <div className="space-y-4" data-id="element-432">
                        {serviceType === 'website-transfer' && <div className="space-y-6" data-id="element-433">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-434">
                              {[{
                        id: 'hasWebsite',
                        label: 'Do you already have a website?'
                      }, {
                        id: 'hasDomain',
                        label: 'Do you have a domain name?'
                      }, {
                        id: 'needTransfer',
                        label: 'Do you need a domain transfer?'
                      }, {
                        id: 'needHosting',
                        label: 'Do you need hosting?'
                      }, {
                        id: 'needNewWebsite',
                        label: 'Do you need a new website?'
                      }, {
                        id: 'needMaintenance',
                        label: 'Do you need website maintenance?'
                      }, {
                        id: 'needMigration',
                        label: 'Do you need website migration?'
                      }].map(q => <div key={q.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-435">
                                  <span className="text-sm font-medium text-slate-700" data-id="element-436">
                                    {q.label}
                                  </span>
                                  <div className="flex gap-2" data-id="element-437">
                                    {['Yes', 'No'].map(option => <button key={option} type="button" onClick={() => setFormData(prev => ({
                            ...prev,
                            [q.id]: option
                          }))} className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${formData[q.id] === option ? 'bg-[#22A39F] text-white' : 'bg-white text-slate-400 border border-slate-200 hover:border-[#22A39F]/30'}`} data-id="element-438">
                                        {option}
                                      </button>)}
                                  </div>
                                </div>)}
                            </div>
                            <textarea placeholder="Any additional details about your website project?" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-[#22A39F]/20 focus:border-[#22A39F] outline-none transition-all min-h-[100px]" value={formData.additionalDetails || ''} onChange={e => setFormData({
                      ...formData,
                      additionalDetails: e.target.value
                    })} data-id="element-439" />
                          </div>}
                        {serviceType === 'internet-audit' && <>
                            <div data-id="element-440">
                              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-441">
                                Current Speed (if known)
                              </label>
                              <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent outline-none bg-surface" placeholder="e.g. 100Mbps, 1Gbps" data-id="element-442" />
                            </div>
                            <div data-id="element-443">
                              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-444">
                                Contract End Date (if known)
                              </label>
                              <input type="date" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent outline-none bg-surface" data-id="element-445" />
                            </div>
                          </>}
                        {serviceType === 'tv-audit' && <>
                            <div data-id="element-446">
                              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-447">
                                Number of TV Boxes
                              </label>
                              <input type="number" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent outline-none bg-surface" placeholder="e.g. 5" data-id="element-448" />
                            </div>
                            <div data-id="element-449">
                              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-450">
                                Must-have Channels
                              </label>
                              <textarea rows={2} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent outline-none bg-surface resize-none" placeholder="e.g. ESPN, CNN, Local News" data-id="element-451" />
                            </div>
                          </>}
                        {serviceType === 'new-service' && <>
                            <div data-id="element-452">
                              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-453">
                                Primary Interest
                              </label>
                              <select className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent outline-none bg-surface appearance-none" data-id="element-454">
                                <option data-id="element-455">Fiber Internet</option>
                                <option data-id="element-456">UCaaS / Phone System</option>
                                <option data-id="element-457">Security / Access Control</option>
                                <option data-id="element-458">Multiple Services</option>
                              </select>
                            </div>
                            <div data-id="element-459">
                              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-460">
                                Number of Users
                              </label>
                              <input type="number" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent outline-none bg-surface" placeholder="e.g. 25" data-id="element-461" />
                            </div>
                          </>}
                        {serviceType === 'relocation' && <>
                            <div data-id="element-462">
                              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-463">
                                New Address
                              </label>
                              <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent outline-none bg-surface" placeholder="123 New Office Way" data-id="element-464" />
                            </div>
                            <div data-id="element-465">
                              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-466">
                                Target Move Date
                              </label>
                              <input type="date" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent outline-none bg-surface" data-id="element-467" />
                            </div>
                          </>}
                        {serviceType === 'general' && <div data-id="element-468">
                            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-469">
                              How can we help?
                            </label>
                            <textarea rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent outline-none bg-surface resize-none" placeholder="Tell us about your needs..." data-id="element-470" />
                          </div>}
                      </div>
                    </motion.div>}

                  {currentStep === 4 && <motion.div key="step4" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-471">
                      <div className="mb-6" data-id="element-472">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-473">
                          Timeline & Urgency
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-474">
                          When would you like to see results?
                        </p>
                      </div>
                      <div className="space-y-4" data-id="element-475">
                        <div data-id="element-476">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-477">
                            How soon do you need this?
                          </label>
                          <select className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface appearance-none" data-id="element-478">
                            <option data-id="element-479">ASAP</option>
                            <option data-id="element-480">Within 30 Days</option>
                            <option data-id="element-481">Researching for Future</option>
                          </select>
                        </div>
                        <div data-id="element-482">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-483">
                            Preferred Contact Method
                          </label>
                          <div className="grid grid-cols-2 gap-3" data-id="element-484">
                            {['Email', 'Phone Call', 'Text Message', 'No Preference'].map(method => <label key={method} className="flex items-center p-3 border border-slate-200 rounded-xl cursor-pointer hover:border-accent transition-colors bg-surface" data-id="element-485">
                                <input type="radio" name="contactMethod" className="w-4 h-4 text-accent" data-id="element-486" />
                                <span className="ml-2 text-sm" data-id="element-487">{method}</span>
                              </label>)}
                          </div>
                        </div>
                      </div>
                    </motion.div>}

                  {currentStep === 5 && <motion.div key="step5" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-488">
                      <div className="mb-6" data-id="element-489">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-490">
                          Final Review
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-491">
                          Ready to submit your request for a {content.title}?
                        </p>
                      </div>
                      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-3" data-id="element-492">
                        <div className="flex justify-between text-sm" data-id="element-493">
                          <span className="text-slate-500" data-id="element-494">Service:</span>
                          <span className="font-bold text-slate-900" data-id="element-495">
                            {content.title}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-496">
                          <span className="text-slate-500" data-id="element-497">Audit Type:</span>
                          <span className="font-bold text-slate-900 capitalize" data-id="element-498">
                            {serviceType.replace('-', ' ')}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-4" data-id="element-499">
                          By clicking submit, you agree to our terms and allow
                          our specialists to contact you regarding your telecom
                          needs.
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Footer Actions */}
                <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between" data-id="element-500">
                  {currentStep > 1 ? <button type="button" onClick={handleBack} className="px-6 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors flex items-center gap-2" data-id="element-501">
                      <ArrowLeftIcon className="w-4 h-4" data-id="element-502" /> Back
                    </button> : <div data-id="element-503"></div>}

                  {currentStep < 5 ? <button type="button" onClick={handleNext} className="bg-accent hover:bg-accent-dark text-white px-8 py-3 rounded-xl font-bold transition-colors shadow-lg shadow-accent/20 flex items-center gap-2" data-id="element-504">
                      Next Step <ArrowRightIcon className="w-4 h-4" data-id="element-505" />
                    </button> : <button type="submit" className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-bold transition-colors shadow-lg flex items-center gap-2" data-id="element-506">
                      Submit Request
                    </button>}
                </div>
              </form>
            </>}
        </div>
      </motion.div>
    </div>;
}